/**
 * Router Configuration
 * @description Vue Router setup with role-based auth guards
 */
import VueRouter from "vue-router";
import routes from "./routes";
import authStore, { UserRoles } from "@/stores/authStore";

const router = new VueRouter({
  mode: "history",
  routes,
  linkExactActiveClass: "active",
  scrollBehavior: (to) => {
    if (to.hash) {
      return { selector: to.hash };
    }
    return { x: 0, y: 0 };
  },
});

/**
 * Returns the default route based on user role
 * @param {String} role - User role
 * @returns {Object} Route object
 */
function getDefaultRouteForRole(role) {
  switch (role) {
    case UserRoles.INVESTOR:
      return { name: "investor-dashboard" };
    case UserRoles.ENTREPRENEUR:
      return { name: "controlcenter" };
    default:
      return { name: "investor-dashboard" };
  }
}

/** Flag to track if auth is initialized */
let authInitialized = false;

/**
 * Ensures auth store is initialized
 * @returns {Promise<void>}
 */
async function ensureAuthInit() {
  if (!authInitialized) {
    await authStore.init();
    authInitialized = true;
  }
}

/**
 * Navigation Guard - Auth and Role Check
 * @description Handles authentication and role-based access control
 */
router.beforeEach(async (to, from, next) => {
  // In der Entwicklungsumgebung schalten wir die komplexen Auth-Redirects ab,
  // damit es beim Arbeiten mit direkten Deep-Links (z.B. entrepreneur/financial-health)
  // nicht zu Runtime-Fehlern im Overlay kommt.
  if (process.env.NODE_ENV === "development") {
    next();
    return;
  }

  // Always ensure auth is initialized before any navigation
  await ensureAuthInit();

  const isAuthenticated = authStore.isAuthenticated;
  const userRole = authStore.role;
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const isAuthPage = to.matched.some((record) => record.meta.isAuthPage);
  const allowedRoles = to.matched
    .filter((record) => record.meta.allowedRoles)
    .flatMap((record) => record.meta.allowedRoles);

  // Debug log for development
  if (process.env.NODE_ENV === "development") {
    console.log("[Router Guard]", {
      to: to.name,
      isAuthenticated,
      userRole,
      requiresAuth,
      allowedRoles,
    });
  }

  // Case 1: Requires auth but not authenticated
  if (requiresAuth && !isAuthenticated) {
    // In der Entwicklungsumgebung lassen wir die Navigation zu,
    // damit beim automatischen Redirect kein Runtime-Error im Overlay erscheint.
    if (process.env.NODE_ENV === "development") {
      console.warn("[Router Guard] Development mode: skipping auth redirect for", to.fullPath);
      next();
      return;
    }

    next({ name: "login", query: { redirect: to.fullPath } });
    return;
  }

  // Case 2: Auth page but already authenticated
  if (isAuthPage && isAuthenticated) {
    const defaultRoute = getDefaultRouteForRole(userRole);
    // Only redirect if not already going to the default route
    if (to.name !== defaultRoute.name) {
      next(defaultRoute);
      return;
    }
  }

  // Case 3: Route requires specific role(s)
  if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
    // Redirect to appropriate dashboard based on actual role
    next(getDefaultRouteForRole(userRole));
    return;
  }

  // Case 4: Home redirect based on role
  if (to.name === "home" && isAuthenticated) {
    next(getDefaultRouteForRole(userRole));
    return;
  }

  // All checks passed
  next();
});

/**
 * After each navigation - Update document title
 */
router.afterEach((to) => {
  const title = to.meta?.title;
  document.title = title ? `${title} | Kemaris` : "Kemaris";
});

export default router;
